import { Platform } from 'react-native';

import { getSkipLegacySession } from '../config/runtimeConfig';
import { getEmailFromAccessToken } from '../biometric/accessTokenClaims';
import { wipeBiometricStateIfUserSwitched } from '../biometric/biometricEnrollment';
import { LoginCredentials, LoginResponse } from '../types';
import * as authApi from '../api/auth';
import * as ssoApi from '../api/sso';
import * as magicLinkApi from '../api/magicLink';
import * as impersonationApi from '../api/impersonation';
import { resolveDestination, resolveNextUrl } from '../utils/resolveDestination';
import { sanitizeRedirectUrl, isZendeskReturnTo } from '../utils/redirect';
import { EXTERNAL_URLS } from '../utils/constants';
import { analytics } from '../utils/analytics';
import {
  storeToken,
  storeRefreshToken,
  storeTokenExpires,
  storeLoginResponse,
  storeNextUrl,
  getToken,
  getRefreshToken,
  getTokenExpires,
  getNextUrl,
  removeNextUrl,
  isRedirectLoop,
  recordRedirect,
  removeToken,
  storeImpersonationContext,
} from './storageService';

async function storeTokens(loginResponse: LoginResponse) {
  await storeLoginResponse(loginResponse);
  if (loginResponse.token) await storeToken(loginResponse.token);
  if (loginResponse.refresh) await storeRefreshToken(loginResponse.refresh);
  if (loginResponse.expires) await storeTokenExpires(loginResponse.expires);
  await wipeBiometricStateIfUserSwitched(getEmailFromAccessToken(loginResponse.token));
}

/**
 * Handle the login flow.
 *
 * Returns { requiresTwoFactor: false } on success, or
 * { requiresTwoFactor: true, lastFourDigits } when 2FA is needed (SMS already sent by Django).
 */
export const handleLogin = async (
  credentials: LoginCredentials,
  nextUrl?: string
): Promise<
  | { requiresTwoFactor: false; token: string; nextApp?: string }
  | { requiresTwoFactor: true; lastFourDigits: string }
> => {
  const result = await authApi.login(credentials);

  if (result.status === 202) {
    return {
      requiresTwoFactor: true,
      lastFourDigits: result.data.last_four_digits,
    };
  }

  await storeTokens(result.data);
  return { requiresTwoFactor: false, token: result.data.token, nextApp: result.data.next_app };
};

/**
 * Verify a 2FA code and complete login.
 * Re-calls /login with the same credentials + the code.
 */
export const handleTwoFactorLogin = async (
  email: string,
  password: string,
  code: string,
  nextUrl?: string
): Promise<{ token?: string; nextApp?: string }> => {
  const result = await authApi.login({ email, password, two_factor_code: code });

  if (result.status === 202) {
    throw new Error('Two-factor verification failed');
  }

  await storeTokens(result.data);
  return { token: result.data.token, nextApp: result.data.next_app };
};

type SessionRestoreResult =
  | { status: 'redirect'; url: string }
  | { status: 'zendesk'; jwt: string; returnTo: string }
  | { status: 'none' };

export type RedirectAfterAuthResult =
  | { kind: 'url'; url: string }
  | { kind: 'zendesk'; jwt: string; returnTo: string };

/**
 * Complete a successful auth flow. Behavior depends on whether the
 * consumer supplied an `onAuthenticated` callback via `LoginRoot`:
 *
 *   - Callback present (native consumer, e.g. VRN): call it with the
 *     token + nextApp. Consumer decides how to route inside the app
 *     (typically: gate on nextApp, refetch the user, replace to root).
 *     The URL-based web redirect is skipped entirely.
 *   - No callback (standalone web deploy): compute a redirect via
 *     redirectAfterAuth, do the Zendesk POST-form handoff if needed,
 *     otherwise store the next URL and navigate the browser.
 *
 * Native without a callback throws — the historical `Linking.openURL`
 * fallback pointed at web paths (`/app/`, `/clients/`, …) and never
 * did anything useful in a RN process.
 */
export const completeAuth = async (opts: {
  token: string;
  urlNextUrl?: string;
  urlReturnTo?: string;
  nextApp?: string;
  onAuthenticated: ((r: { token: string; nextApp?: string }) => void | Promise<void>) | null;
}): Promise<void> => {
  const { token, urlNextUrl, urlReturnTo, nextApp, onAuthenticated } = opts;

  if (onAuthenticated) {
    await onAuthenticated({ token, nextApp });
    return;
  }

  if (Platform.OS !== 'web') {
    throw new Error(
      'Login mounted on native without an onAuthenticated callback — ' +
        'pass one to <LoginRoot onAuthenticated={...}>.'
    );
  }

  const redirect = await redirectAfterAuth({ token, urlNextUrl, urlReturnTo, nextApp });
  if (redirect.kind === 'zendesk') {
    submitZendeskHandoff(redirect.jwt, redirect.returnTo);
    return;
  }
  await storeNextUrl(redirect.url);
  window.location.href = redirect.url;
};

/**
 * Submit a hidden auto-submitted POST form to Zendesk's /access/jwt endpoint.
 *
 * The JWT travels in the POST body rather than the URL, so it stays out of
 * browser history, the URL bar, Zendesk's access logs, and anything that
 * reads URLs (extensions, telemetry). Same SAML-POST-binding pattern.
 *
 * Web-only — falls back to a URL-based redirect on native platforms, where
 * Zendesk Web SSO doesn't apply in practice anyway.
 */
export function submitZendeskHandoff(jwt: string, returnTo: string): void {
  if (!jwt || !returnTo) {
    analytics.trackEvent('zendesk_handoff_failed', { stage: 'submit_missing_inputs' });
    return;
  }
  const action = new URL('/access/jwt', EXTERNAL_URLS.ZENDESK).toString();

  if (typeof document === 'undefined') {
    const url = new URL(action);
    url.searchParams.set('jwt', jwt);
    url.searchParams.set('return_to', returnTo);
    if (typeof window !== 'undefined') {
      window.location.href = url.toString();
    }
    return;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  for (const [name, value] of Object.entries({ jwt, return_to: returnTo })) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  // Detach on next tick — form.submit() normally navigates the page so this
  // is a no-op, but a popup blocker or extension can swallow the submit and
  // leave the form orphaned in the DOM. Cleanup keeps the tree tidy.
  setTimeout(() => form.parentNode?.removeChild(form), 0);
}

/**
 * Compute the final post-auth redirect.
 *
 * Zendesk branch: when ?return_to points at support.vida.com, mint a Zendesk
 * JWT and return a 'zendesk' result so the caller can hand off via POST form
 * to {ZENDESK}/access/jwt — that's what establishes a Zendesk session for
 * support.vida.com (the old ZendeskSSOLoginView flow).
 *
 * Regular branch: resolve next_app via PostHog flags, sanitize the URL ?next=,
 * fall back to the API-derived app destination — return a 'url' result.
 */
export const redirectAfterAuth = async (opts: {
  token: string;
  urlNextUrl?: string;
  urlReturnTo?: string;
  nextApp?: string;
}): Promise<RedirectAfterAuthResult> => {
  const { token, urlNextUrl, urlReturnTo, nextApp } = opts;

  // Gate the Zendesk branch to web — the POST-form handoff requires the DOM,
  // and the native app doesn't carry a browser session that benefits from the
  // Zendesk handshake anyway.
  if (Platform.OS === 'web' && token && urlReturnTo && isZendeskReturnTo(urlReturnTo)) {
    try {
      const jwt = await authApi.obtainZendeskSSOToken(token);
      return { kind: 'zendesk', jwt, returnTo: urlReturnTo };
    } catch (err) {
      analytics.trackEvent('zendesk_handoff_failed', { stage: 'post_login_mint' });
    }
  }

  const appDestination = nextApp ? await resolveDestination(nextApp) : undefined;
  const fallback = await resolveDestination('member_app');
  const resolvedNextUrl = await resolveNextUrl(urlNextUrl, nextApp);
  return {
    kind: 'url',
    url: sanitizeRedirectUrl(resolvedNextUrl || appDestination, fallback),
  };
};

/**
 * Check for an existing valid session and return a redirect URL if one is found.
 * - Valid token → redirect immediately
 * - Expired token + refresh token → silently refresh, then redirect
 * - No valid auth → clear storage, return 'none' (show login form)
 *
 * URL params (?next=, ?return_to=) take precedence over the stored next URL —
 * they reflect the *current* navigation intent, while storage is a fallback
 * for cross-redirect persistence from a prior session.
 */
export const attemptSessionRestore = async (
  fallbackUrl: string,
  opts?: { urlNextUrl?: string; urlReturnTo?: string }
): Promise<SessionRestoreResult> => {
  const token = await getToken();
  if (!token) return { status: 'none' };

  const expires = await getTokenExpires();
  const isValid = expires ? Date.now() < new Date(expires).getTime() - 30_000 : false;

  const storedNextUrl = await getNextUrl();
  await removeNextUrl();
  // URL next wins over stored next. Sanitize BOTH so neither a tampered
  // sessionStorage entry nor an attacker-controlled query param can produce
  // an open redirect — sanitizeRedirectUrl only allows *.vida.com (or paths).
  const sanitizedUrlNext = opts?.urlNextUrl
    ? sanitizeRedirectUrl(opts.urlNextUrl, '')
    : '';
  const sanitizedStoredNext = storedNextUrl
    ? sanitizeRedirectUrl(storedNextUrl, '')
    : '';
  const destination = sanitizedUrlNext || sanitizedStoredNext || fallbackUrl;
  const zendeskReturnTo = isZendeskReturnTo(opts?.urlReturnTo) ? opts!.urlReturnTo! : undefined;

  // In dual-mint mode (SKIP_LEGACY_SESSION=false), validate the Django
  // session cookie alongside the JWT before redirecting. Skipped in
  // Apigee-native mode since legacy apps are not involved.
  const needsCookieCheck = !getSkipLegacySession();

  // Guard against a redirect loop where the destination bounces the user back to
  // /login (e.g. SSO resolves to "/" which the SPA Router routes back here).
  // Without this, attemptSessionRestore would re-redirect on every mount until
  // Apigee spike-arrest revokes the session.
  if (await isRedirectLoop(destination)) {
    console.warn('attemptSessionRestore: redirect loop detected, falling back to login form', destination);
    await removeToken();
    return { status: 'none' };
  }

  if (isValid) {
    if (needsCookieCheck && !(await authApi.validateSession(token))) {
      await removeToken();
      return { status: 'none' };
    }
    if (zendeskReturnTo) {
      try {
        const jwt = await authApi.obtainZendeskSSOToken(token);
        return { status: 'zendesk', jwt, returnTo: zendeskReturnTo };
      } catch (err) {
        analytics.trackEvent('zendesk_handoff_failed', { stage: 'session_restore' });
        // Fall through to regular destination so the user isn't stranded.
      }
    }
    await recordRedirect(destination);
    return { status: 'redirect', url: destination };
  }

  const refresh = await getRefreshToken();
  if (!refresh) {
    await removeToken();
    return { status: 'none' };
  }

  try {
    const {
      token: newToken,
      refresh: newRefresh,
      expires: newExpires,
    } = await authApi.refreshToken(refresh);
    await storeToken(newToken);
    await storeRefreshToken(newRefresh);
    await storeTokenExpires(newExpires);
    if (needsCookieCheck && !(await authApi.validateSession(newToken))) {
      await removeToken();
      return { status: 'none' };
    }
    if (zendeskReturnTo) {
      try {
        const jwt = await authApi.obtainZendeskSSOToken(newToken);
        return { status: 'zendesk', jwt, returnTo: zendeskReturnTo };
      } catch (err) {
        analytics.trackEvent('zendesk_handoff_failed', { stage: 'after_token_refresh' });
      }
    }
    await recordRedirect(destination);
    return { status: 'redirect', url: destination };
  } catch {
    await removeToken();
    return { status: 'none' };
  }
};

/**
 * Resend 2FA code by re-calling login without a code.
 * Django sends a fresh SMS when two_factor_code is absent.
 */
export const resendTwoFactorCode = async (
  credentials: LoginCredentials
): Promise<void> => {
  await authApi.login(credentials);
};

/**
 * Redeem an SSO callback code for tokens and resolve the final destination.
 *
 * Called from /login/sso-callback after the SSO service redirects the browser
 * with ?code=<opaque>. The exchange returns tokens (stored in sessionStorage)
 * plus destination metadata:
 *   - relay_state: validated same-origin path from the SAML RelayState (per
 *     Okta tile). Used when present.
 *   - next_app: role-derived destination key from Django. Used as fallback.
 */
export const handleSsoCallback = async (
  code: string
): Promise<{ token: string; nextApp?: string; destination: string }> => {
  const result = await ssoApi.ssoExchange(code);

  // Apigee returns relay_state: "" when the SAML assertion had no RelayState
  // (customer-org tiles typically don't set one). Treat empty the same as
  // missing — otherwise destination becomes "" which sanitizeRedirectUrl
  // turns into "/" and the SPA Router bounces back to /login forever.
  const destination =
    result.relay_state || (await resolveDestination(result.next_app || 'member_app'));

  await storeTokens({
    success: result.success,
    token: result.token,
    refresh: result.refresh,
    expires: result.expires,
    next_app: result.relay_state ? undefined : result.next_app,
  });

  await storeNextUrl(destination);

  // token + nextApp surfaced so native callers can plumb them through
  // completeAuth() to the consumer's onAuthenticated callback; destination
  // stays on the return so the web SPA can navigate the browser directly
  // (skipping redirectAfterAuth — RelayState was already sanitized upstream).
  return { token: result.token, nextApp: result.next_app, destination };
};

export const handleImpersonationCallback = async (
  code: string
): Promise<{ token: string; destination: string }> => {
  const result = await impersonationApi.impersonationExchange(code);

  await storeToken(result.token);
  await storeTokenExpires(result.expires);
  await storeImpersonationContext(result.impersonating, result.impersonation_id);

  const destination = await resolveDestination('member_app');
  await storeNextUrl(destination);
  return { token: result.token, destination };
};

/**
 * Redeem a magic-link sesame token for Apigee-minted JWTs, store them, and
 * return the token + next_app so the caller can drive redirectAfterAuth.
 *
 * Django's SesameLogin blocks 2FA-enabled users and expired/reused tokens
 * server-side; Apigee surfaces both as 401, which propagates as a thrown
 * axios error here — the caller should treat that as "link invalid" and
 * bounce back to the magic-link entry screen.
 */
export const handleTokenLoginExchange = async (
  loginToken: string,
): Promise<{ token: string; nextApp?: string }> => {
  const result = await magicLinkApi.exchangeMagicLinkToken({ loginToken });
  await storeTokens(result);
  return { token: result.token, nextApp: result.next_app };
};
